import {Popover} from '@dropbox/dig-components/popover';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {TeamLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {useState} from 'react';
import {
  EMPTY_VALUE,
  type WorkstreamStaffingState,
} from 'views/workstreams/detail/WorkstreamStaffing';

import {StaffingStrip} from './StaffingStrip';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

/** Whether the properties line offers Staffing: once the workstream is known to be in the half. */
export const offersStaffing = (staffing: WorkstreamStaffingState) =>
  staffing.status !== 'loading' && staffing.status !== 'outside_half';

// What the popover shows: the strip for finance readers (dashes while loading),
// otherwise why there are no numbers.
const StaffingPopoverBody = ({staffing}: {staffing: WorkstreamStaffingState}) => {
  switch (staffing.status) {
    case 'ready':
      return <StaffingStrip staffing={staffing.staffing} />;
    case 'loading':
      return <StaffingStrip staffing={{values: null, headcount: EMPTY_VALUE}} />;
    // Not offered outside the half (offersStaffing).
    case 'outside_half':
      return null;
    case 'denied':
      return (
        <Text size="small" color="subtle">
          Staffing is visible to Finance admins.
        </Text>
      );
    case 'error':
      return (
        <Text size="small" color="subtle">
          Couldn&apos;t load staffing. Try again later.
        </Text>
      );
  }
};

// "10 FTE" once there's a headcount; otherwise just "Staffing".
const headcountLabel = (staffing: WorkstreamStaffingState) =>
  staffing.status === 'ready' && staffing.staffing.headcount !== EMPTY_VALUE
    ? t('workstream_staffing_fte', {value: staffing.staffing.headcount})
    : null;

/**
 * /workstreams detail: the staffing rollup ("| 10 FTE") joined onto the end of
 * the people chip; click opens the staffing strip (headcount, allocation and geo
 * splits) in a popover, or says why there are no numbers.
 */
export const StaffingButton = ({
  staffing,
  workstreamId,
}: {
  staffing: WorkstreamStaffingState;
  workstreamId: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const headcount = headcountLabel(staffing);

  return (
    <>
      {/* The people chip's own hairline, continued. */}
      <Box backgroundColor="Border Subtle" style={{width: 1, height: 16}} />
      <Popover
        triggerStrategy="click"
        open={isOpen}
        onToggle={(open) => {
          if (open) logWorkstreamsInteraction('staffing_opened', {workstream_id: workstreamId});
          setIsOpen(open);
        }}
        placement="bottom-start"
        offsetDistance={8}
      >
        <Popover.Trigger asChild>
          {/* Shaped like the people chip's segments beside it (32px, hover tint). */}
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={
              headcount ? `${t('workstream_staffing')}: ${headcount}` : t('workstream_staffing')
            }
            onMouseDown={(event) => event.preventDefault()}
            className={atoms({
              display: 'flex',
              alignItems: 'center',
              borderRadius: 'Medium',
              cursor: 'pointer',
              color: 'inherit',
              backgroundColor: {
                default: isOpen ? 'Opacity Surface - State 1' : 'transparent',
                hover: 'Opacity Surface - State 1',
              },
            })}
            style={{height: 32, padding: 0, border: 0}}
          >
            <Box display="flex" alignItems="center" paddingX="8" style={{gap: 6}}>
              <UIIcon src={TeamLine} size="small" className={atoms({color: 'Text Subtle'})} />
              <Text size="small">{headcount ?? t('workstream_staffing')}</Text>
            </Box>
          </button>
        </Popover.Trigger>
        <Popover.Overlay>
          <Popover.Content>
            <Popover.Body>
              <StaffingPopoverBody staffing={staffing} />
            </Popover.Body>
          </Popover.Content>
        </Popover.Overlay>
      </Popover>
    </>
  );
};
